import { type ReactNode, createContext, useContext, useState } from 'react';

import {
  AnimationCanvas,
  type AnimationCanvasProps,
} from '@suntex.core/animation';

import { playerBadgeComposition } from './playerBadgeComposition';

/** Symbol library of the PlayerBadge Animate export. */
export type BadgeLibrary = Parameters<
  AnimationCanvasProps['onReady']
>[0]['lib'];

const BadgeLibraryContext = createContext<BadgeLibrary | undefined>(undefined);

/** The loaded PlayerBadge Animate library, or undefined while loading. */
export function useBadgeLibrary() {
  return useContext(BadgeLibraryContext);
}

/**
 * Loads the PlayerBadge Animate library once so any number of
 * {@link BadgeCanvas} children can draw from it without their own loader.
 */
export function BadgeLibraryProvider({ children }: { children: ReactNode }) {
  const [lib, setLib] = useState<BadgeLibrary>();

  return (
    <BadgeLibraryContext.Provider value={lib}>
      <div aria-hidden style={hiddenStyle}>
        <AnimationCanvas
          {...playerBadgeComposition}
          width={1}
          height={1}
          onReady={({ lib, stage }) => {
            // The loader stage draws nothing; stop it ticking
            createjs.Ticker.removeEventListener(
              'tick',
              stage as unknown as () => void,
            );
            setLib(lib);
          }}
        />
      </div>
      {children}
    </BadgeLibraryContext.Provider>
  );
}

const hiddenStyle = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
} as const;
